import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react'
import { Box } from '@mui/material'
import { EmtSelect, EmtButton, EmtTypography, EmtIcon } from 'emt-components-v2'
import { DatePickerField } from '@/components/PetSitter/EmtCalendar/DatePickerField'
import { generateTimeOptions, timeSelectMenuProps } from './utils/timeUtils'

interface DateTimeFormProps {
  onSave: (instance: DateTimeInstance) => void
  onCancel: (() => void) | boolean
  showCancel?: boolean
  initialValues: DateTimeInstance
  formType: 'promenade' | 'garde' | 'visite'
  title: string
  selectedService?: 'promenade' | 'garde-chez-eux' | 'visite' | 'garde-chez-vous'
  showGardeTimes?: boolean
}

interface DateTimeInstance {
  date?: string
  dateStart?: string
  dateEnd?: string
  timeStart?: string
  timeEnd?: string
  serviceDayCount?: number
  serviceDuration?: string
  timePeriods?: Array<{ timeStart?: string; timeEnd?: string; serviceDuration?: string }>
}

const serviceDurationOptions = [
  { value: 'half_hour', label: '30 minutes' },
  { value: 'hour', label: '60 minutes' },
]

const DateTimeForm = forwardRef<any, DateTimeFormProps>(
  ({ onSave, onCancel, showCancel, initialValues, formType, title, selectedService, showGardeTimes }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [values, setValues] = useState<DateTimeInstance>({
      ...initialValues,
    })

    const lastEmittedRef = useRef<string>('')

    const [errors, setErrors] = useState<Record<string, any>>({
      timePeriods: [], // Initialize errors for time periods
    })

    useEffect(() => {
      if (initialValues.dateStart || initialValues.dateEnd || initialValues.timeStart || initialValues.timeEnd) {
        setValues({
          ...initialValues,
        })
      } else {
        setValues({
          dateStart: '',
          timeStart: '',
          dateEnd: '',
          timeEnd: '',
        })
      }
      // Reset errors when formType changes
      setErrors({ timePeriods: [] })
    }, [initialValues, selectedService])

    const onFieldChange = (key: string, value: any) => {
      setValues((prevValues) => ({ ...prevValues, [key]: value }))
      checkField(key, value)
    }

    const checkField = (key: string, value: any = values[key], showErrors = true): boolean => {
      let error = ''
      switch (key) {
        case 'date':
          if (!value && formType !== 'garde') {
            error = 'Veuillez sélectionner une date'
          }
          break
        case 'dateStart':
          if (!value) {
            error = 'Veuillez sélectionner une date de début'
          }
          break
        case 'dateEnd':
          if (!value) {
            error = 'Veuillez sélectionner une date de fin'
          }
          break
        case 'timeStart':
          if (!value && showGardeTimes) {
            error = 'Veuillez sélectionner une heure de début'
          }
          break
        case 'timeEnd':
          if (!value && showGardeTimes) {
            error = 'Veuillez sélectionner une heure de fin'
          } else if (
            formType === 'garde' &&
            values.dateStart === values.dateEnd &&
            values.timeStart &&
            value <= values.timeStart
          ) {
            error = "L'heure de fin doit être après l'heure de début"
          }
          break
        // case 'serviceDayCount':
        //   if (!value) {
        //     error = 'Veuillez sélectionner une quantité de visites quotidienne'
        //   }
        //   break
        // case 'timePeriods':
        //   // Check each time period for errors
        //   const newTimePeriodErrors = (value || []).map((timePeriod: any) => {
        //     const periodErrors: {
        //       timeStart?: string
        //       timeEnd?: string
        //       serviceDuration?: string
        //     } = {}

        //     // Check for timeStart error
        //     if (!timePeriod.timeStart && showGardeTimes) {
        //       periodErrors.timeStart = 'Veuillez sélectionner une heure de début'
        //     }

        //     // Check for timeEnd or serviceDuration error
        //     if (!timePeriod.timeEnd && !timePeriod.serviceDuration && !timePeriod.serviceDuration && showGardeTimes) {
        //       periodErrors.timeEnd = 'Veuillez sélectionner une heure de fin'
        //       periodErrors.serviceDuration = 'Veuillez sélectionner une durée de visite'
        //       periodErrors.serviceDuration = 'Veuillez sélectionner une durée de promenade'
        //     }

        //     return periodErrors
        //   })
        //   // Update the errors state for timePeriods
        //   if (showErrors) {
        //     setErrors((prevErrors) => ({ ...prevErrors, timePeriods: newTimePeriodErrors }))
        //   }

        //   // Check if there are any errors in the time periods
        //   const hasTimePeriodErrors = newTimePeriodErrors.some(
        //     (periodError) => periodError.timeStart || periodError.timeEnd,
        //   )
        //   return !hasTimePeriodErrors
        default:
          break
      }

      if (showErrors && key !== 'timePeriods') {
        setErrors((prevErrors) => ({ ...prevErrors, [key]: error }))
      }

      return !error
    }

    const validateForm = (showErrors = true) => {
      let isValid = true
      const fieldsToValidate = Object.keys(values)
      fieldsToValidate.forEach((key) => {
        if (!checkField(key, (values as any)[key], showErrors)) {
          isValid = false
        }
      })
      return isValid
    }

    /**
     * Autosave: si le formulaire est valide et que les valeurs ont changé,
     * on pousse l'instance au parent via onSave(values).
     * Le parent met à jour ses slots -> l'effet existant déclenchera updateCart
     * uniquement si l'instance est complète (déjà géré côté parent).
     */
    useEffect(() => {
      // stringify léger pour comparer la "shape" du formulaire
      const snapshot = JSON.stringify(values)
      // Valide sans effacer les erreurs déjà affichées inutilement
      const ok = validateForm(false)
      if (ok && snapshot !== lastEmittedRef.current) {
        onSave(values)
        lastEmittedRef.current = snapshot
      }
      // Si non ok, ne pas émettre et laisser l'utilisateur corriger
    }, [values]) // eslint-disable-line react-hooks/exhaustive-deps

    const addTimePeriod = () => {
      setValues((prevValues) => ({
        ...prevValues,
        timePeriods: [...(prevValues.timePeriods || []), { timeStart: '', timeEnd: '' }],
      }))
      setErrors((prevErrors) => ({
        ...prevErrors,
        timePeriods: [...(prevErrors.timePeriods || []), {}],
      }))
    }

    const removeTimePeriod = (index: number) => {
      setValues((prevValues) => ({
        ...prevValues,
        timePeriods: prevValues.timePeriods?.filter((_, i) => i !== index),
      }))
      setErrors((prevErrors) => ({
        ...prevErrors,
        timePeriods: prevErrors.timePeriods.filter((_, i) => i !== index),
      }))
    }

    const updateTimePeriod = (
      index: number,
      key: 'timeStart' | 'timeEnd' | 'serviceDuration' | 'serviceDuration',
      value: string,
    ) => {
      const newTimePeriods = [...(values.timePeriods || [])]
      newTimePeriods[index] = {
        ...newTimePeriods[index],
        [key]: value,
      }
      onFieldChange('timePeriods', newTimePeriods)
    }

    const getMinDate = () => {
      const today = new Date()
      return today.toISOString().split('T')[0]
    }

    const getMinEndDate = () => {
      if (values.dateStart) {
        return values.dateStart
      }
      return getMinDate()
    }

    const getTimeOptions = (selectedDate?: string) => {
      return generateTimeOptions('00:00', '23:30', 30, selectedDate)
    }

    const renderDateField = () => (
      <DatePickerField
        label="Date"
        value={values.date}
        onChange={(selectedDate) => {
          const minDate = getMinDate()
          if (selectedDate >= minDate) {
            onFieldChange('date', selectedDate)
            // Clear any previous error
            setErrors((prev) => ({ ...prev, date: '' }))
          } else {
            // Set error for past date
            setErrors((prev) => ({ ...prev, date: 'La date ne peut pas être dans le passé' }))
          }
        }}
        error={!!errors.date}
        helperText={errors.date}
        min={getMinDate()}
      />
    )

    const renderFields = () => {
      switch (formType) {
        case 'promenade':
          return (
            <>
              {renderDateField()}
              {values.timePeriods?.map((timePeriod, index) => (
                <Box key={index} sx={{ display: 'flex', gap: '10px' }}>
                  <Box className="emtselect-item" sx={{ width: '100%', minWidth: 0 }}>
                    <EmtSelect
                      // @ts-expect-error
                      color="purple"
                      inputLabel="Heure de début"
                      options={getTimeOptions(values.date)}
                      MenuProps={timeSelectMenuProps}
                      value={timePeriod.timeStart}
                      onChange={(value) => updateTimePeriod(index, 'timeStart', value as any)}
                      error={!!errors.timePeriods[index]?.timeStart}
                      helperText={errors.timePeriods[index]?.timeStart}
                    />
                  </Box>
                  <Box className="emtselect-item" sx={{ width: '100%', minWidth: 0 }}>
                    <EmtSelect
                      // @ts-expect-error
                      color="purple"
                      inputLabel="Durée de la promenade"
                      options={serviceDurationOptions}
                      value={timePeriod.serviceDuration}
                      onChange={(value) => updateTimePeriod(index, 'serviceDuration', value as any)}
                      error={!!errors.timePeriods[index]?.serviceDuration}
                      helperText={errors.timePeriods[index]?.serviceDuration}
                    />
                  </Box>
                  {index > 0 && (
                    <Box
                      sx={{
                        alignSelf: 'center',
                      }}
                    >
                      <EmtButton
                        variant="outlined"
                        color="purple"
                        size="large"
                        sx={{
                          width: '43px',
                          height: '43px',
                          padding: 0,
                          flexShrink: 0,
                          minWidth: 0,
                        }}
                        onClick={() => removeTimePeriod(index)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <EmtIcon className="emt-bin" />
                        </Box>
                      </EmtButton>
                    </Box>
                  )}
                </Box>
              ))}

              <Box
                onClick={addTimePeriod}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  marginTop: '16px',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#AE59C6',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <EmtIcon
                    className="emt-add-small"
                    sx={{
                      fontSize: '17px',
                      color: '#fff',
                    }}
                  />
                </Box>
                <EmtTypography
                  variant="body2"
                  fontWeight={700}
                  color="#AE59C6"
                  sx={{
                    cursor: 'pointer',
                    display: 'inline-block',
                    textDecoration: 'underline',
                  }}
                >
                  Ajouter un horaire
                </EmtTypography>
              </Box>
            </>
          )

        case 'garde':
          return (
            <>
              <Box sx={{ display: 'grid', gap: '10px', gridTemplateColumns: '1fr 1fr' }}>
                <DatePickerField
                  label="Date de début"
                  value={values.dateStart}
                  onChange={(newStartDate) => {
                    const minDate = getMinDate()
                    if (newStartDate >= minDate) {
                      onFieldChange('dateStart', newStartDate)
                      // Reset end date if it exists and is before new start date
                      if (values.dateEnd && values.dateEnd < newStartDate) {
                        onFieldChange('dateEnd', '')
                      }
                      // Clear any previous error
                      setErrors((prev) => ({ ...prev, dateStart: '' }))
                    } else {
                      // Set error for past date
                      setErrors((prev) => ({ ...prev, dateStart: 'La date ne peut pas être dans le passé' }))
                    }
                  }}
                  error={!!errors.dateStart}
                  helperText={errors.dateStart}
                  min={getMinDate()}
                />
                <DatePickerField
                  label="Date de fin"
                  value={values.dateEnd}
                  onChange={(selectedEndDate) => {
                    const minDate = getMinDate()
                    const minEndDate = getMinEndDate()
                    if (selectedEndDate >= minEndDate) {
                      onFieldChange('dateEnd', selectedEndDate)
                      // Clear any previous error
                      setErrors((prev) => ({ ...prev, dateEnd: '' }))
                    } else if (selectedEndDate < minDate) {
                      // Set error for past date
                      setErrors((prev) => ({ ...prev, dateEnd: 'La date ne peut pas être dans le passé' }))
                    } else {
                      // Set error for invalid date
                      setErrors((prev) => ({
                        ...prev,
                        dateEnd: 'La date de fin doit être postérieure à la date de début',
                      }))
                    }
                  }}
                  error={!!errors.dateEnd}
                  helperText={errors.dateEnd}
                  min={getMinEndDate()}
                />
              </Box>
              {showGardeTimes && (
                <Box sx={{ display: 'flex', gap: '10px' }}>
                  <Box className="emtselect-item" sx={{ width: '100%', minWidth: 0 }}>
                    <EmtSelect
                      // @ts-expect-error
                      color="purple"
                      inputLabel="Heure de début"
                      options={getTimeOptions(values.dateStart)}
                      MenuProps={timeSelectMenuProps}
                      value={values.timeStart}
                      onChange={(value) => onFieldChange('timeStart', value)}
                      error={!!errors.timeStart}
                      helperText={errors.timeStart}
                    />
                  </Box>
                  <Box className="emtselect-item" sx={{ width: '100%', minWidth: 0 }}>
                    <EmtSelect
                      // @ts-expect-error
                      color="purple"
                      inputLabel="Heure de fin"
                      options={getTimeOptions(values.dateEnd)}
                      MenuProps={timeSelectMenuProps}
                      value={values.timeEnd}
                      onChange={(value) => onFieldChange('timeEnd', value)}
                      error={!!errors.timeEnd}
                      helperText={errors.timeEnd}
                    />
                  </Box>
                </Box>
              )}
            </>
          )
        case 'visite':
          return (
            <>
              {renderDateField()}
              {showGardeTimes &&
                values.timePeriods?.map((timePeriod, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: '10px' }}>
                    <Box className="emtselect-item" sx={{ width: '100%', minWidth: 0 }}>
                      <EmtSelect
                        // @ts-expect-error
                        color="purple"
                        inputLabel="Heure de début"
                        options={getTimeOptions(values.date)}
                        MenuProps={timeSelectMenuProps}
                        value={timePeriod.timeStart}
                        onChange={(value) => updateTimePeriod(index, 'timeStart', value as any)}
                        error={!!errors.timePeriods[index]?.timeStart}
                        helperText={errors.timePeriods[index]?.timeStart}
                      />
                    </Box>
                    <Box className="emtselect-item" sx={{ width: '100%', minWidth: 0 }}>
                      <EmtSelect
                        // @ts-expect-error
                        color="purple"
                        inputLabel="Durée de la visite"
                        options={serviceDurationOptions}
                        value={timePeriod.serviceDuration}
                        onChange={(value) => updateTimePeriod(index, 'serviceDuration', value as any)}
                        error={!!errors.timePeriods[index]?.serviceDuration}
                        helperText={errors.timePeriods[index]?.serviceDuration}
                      />
                    </Box>
                    {index > 0 && (
                      <Box
                        sx={{
                          alignSelf: 'center',
                        }}
                      >
                        <EmtButton
                          variant="outlined"
                          color="purple"
                          size="large"
                          sx={{
                            width: '43px',
                            height: '43px',
                            padding: 0,
                            flexShrink: 0,
                            minWidth: 0,
                          }}
                          onClick={() => removeTimePeriod(index)}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <EmtIcon className="emt-bin" />
                          </Box>
                        </EmtButton>
                      </Box>
                    )}
                  </Box>
                ))}
              <Box
                onClick={addTimePeriod}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  marginTop: '16px',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#AE59C6',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <EmtIcon
                    className="emt-add-small"
                    sx={{
                      fontSize: '17px',
                      color: '#fff',
                    }}
                  />
                </Box>
                <EmtTypography
                  variant="body2"
                  fontWeight={700}
                  color="#AE59C6"
                  sx={{
                    cursor: 'pointer',
                    display: 'inline-block',
                    textDecoration: 'underline',
                  }}
                >
                  Ajouter un horaire
                </EmtTypography>
              </Box>
            </>
          )
      }
    }

    useImperativeHandle(ref, () => ({
      validateAndSave: async () => {
        if (validateForm()) {
          onSave(values)
          setValues({
            date: '',
            timeStart: '',
            timeEnd: '',
            dateStart: '',
            dateEnd: '',
            serviceDayCount: 1,
            serviceDuration: '',
            timePeriods: [],
          })
          setErrors({ timePeriods: [] })
          lastEmittedRef.current = ''
          return true
        }
        return false
      },

      validateOnly: () => validateForm(),

      expandInstances: (service: 'promenade' | 'garde' | 'visite') => {
        // Hard validate first; if invalid, return empty and let parent scroll to error
        // if (!validateForm()) return []

        // Garde (range)
        if (service === 'garde') {
          const { dateStart, dateEnd, timeStart, timeEnd } = values
          return [
            {
              dateStart,
              dateEnd,
              timeStart,
              timeEnd,
            },
          ]
        }

        // One-shot promenade/visite (this non-recurrent form supports them too)
        if (service === 'promenade') {
          return [
            {
              date: values.date,
              timePeriods: (values.timePeriods || [])
                .filter((tp) => tp?.timeStart && tp?.serviceDuration)
                .map((tp) => ({ timeStart: tp.timeStart, serviceDuration: tp.serviceDuration })),
            },
          ]
        }
        // visite
        return [
          {
            date: values.date,
            timePeriods: (values.timePeriods || [])
              .filter((tp) => tp?.timeStart && tp?.serviceDuration)
              .map((tp) => ({ timeStart: tp.timeStart, serviceDuration: tp.serviceDuration })),
          },
        ]
      },
      container: containerRef,
    }))

    return (
      <>
        <Box
          ref={containerRef}
          // sx={{ border: '1px solid #AE59C6', borderRadius: '18px', padding: '18px 24px' }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {title && (
              <Box>
                <EmtTypography variant="body1" fontWeight="700">
                  {title}
                </EmtTypography>
              </Box>
            )}
            {renderFields()}
          </Box>
          {onCancel && showCancel && (
            <Box sx={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
              <EmtButton color="purple" variant="outlined" onClick={onCancel}>
                Supprimer
              </EmtButton>
            </Box>
          )}
        </Box>
      </>
    )
  },
)

DateTimeForm.displayName = 'DateTimeForm'

export default DateTimeForm
